import { useCallback, useMemo, useState } from 'react';
import { Table, ButtonVariants } from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader } from '@/hooks';
import { AddAttachmentModal, SummaryTiles } from '@/components/common';
import { ATTACHMENT_TARGETS } from '@/lib/api/attachmentsApi';
import { buildTableLabels } from '@/config/tableLabels';
import { ALLOCATION_ACTION_VISIBILITY, hiddenUnless } from '@/config/allocationActions';
import { AllocationConfirmDialog, ALLOCATION_CONFIRM } from '@/slices/allocations';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetOrderAllocationsQuery } from '../api/ordersApi';
import { buildOrderAllocationColumns } from '../constants/orderAllocationColumns';
import { buildUnitHeaderOptions } from '../constants/orderUnits';
import { EMPTY_LIST } from '@/lib/utils';

const getAllocationId = (row) => row.id;

// Table sizes this from every rowAction label, but `hidden` means at most two show — mirrors AllocationsPage.jsx.
const ACTIONS_WIDTH = 358;

/**
 * Props:
 *   module                  the active module route segment
 *   orderId                 the order's database id
 *   allocationsSummary      { allocatedQuantity, remainingToAllocateQuantity }, display-ready
 *   allocatedGhgSavings     the order's allocated GHG emission savings (avg.), display-ready
 */
export const AllocationDetailsTabView = ({
  module,
  orderId,
  allocationsSummary,
  allocatedGhgSavings,
}) => {
  const t = useTranslations();
  const {
    data: rows = EMPTY_LIST,
    isFetching,
    isError,
  } = useGetOrderAllocationsQuery({
    module,
    id: orderId,
  });

  const { unitHeaderSelect, contextValue } = useUnitTypeHeader(rows, buildUnitHeaderOptions, t);

  // TODO: implement action — the declaration/shipment-link flows land with their own features.
  const handleGenerateDeclaration = useCallback(() => {}, []);
  const handleDownloadDeclaration = useCallback(() => {}, []);
  const handleEditDeclaration = useCallback(() => {}, []);
  const handleUpdateShipmentDetails = useCallback(() => {}, []);
  const handleLinkExistingShipment = useCallback(() => {}, []);
  const [attachmentRow, setAttachmentRow] = useState(null);
  const handleAddAttachments = useCallback((row) => setAttachmentRow(row), []);
  const handleCloseAttachment = useCallback(() => setAttachmentRow(null), []);
  const handleViewDeclaration = useCallback(() => {}, []);
  const [confirm, setConfirm] = useState(null);
  const handleMarkRejected = useCallback(
    (row) => setConfirm({ kind: ALLOCATION_CONFIRM.reject, row }),
    [],
  );
  const handleRevoke = useCallback(
    (row) => setConfirm({ kind: ALLOCATION_CONFIRM.revoke, row }),
    [],
  );
  const handleCloseConfirm = useCallback(() => setConfirm(null), []);

  const columns = useMemo(
    () => buildOrderAllocationColumns(t, { unitHeaderSelect }),
    [t, unitHeaderSelect],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const rowActions = useMemo(
    () => [
      {
        id: 'generate-declaration',
        label: t('ALLOCATIONS_ACTION_GENERATE_DECLARATION'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.generateDeclaration),
        onClick: handleGenerateDeclaration,
      },
      {
        id: 'download-declaration',
        label: t('ALLOCATIONS_ACTION_DOWNLOAD_DECLARATION'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.downloadDeclaration),
        onClick: handleDownloadDeclaration,
      },
      {
        id: 'edit-declaration',
        label: t('ALLOCATIONS_ACTION_EDIT_DECLARATION'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.editDeclaration),
        onClick: handleEditDeclaration,
      },
      {
        id: 'update-shipment-details',
        label: t('ALLOCATIONS_ACTION_UPDATE_SHIPMENT_DETAILS'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.updateShipmentDetails),
        onClick: handleUpdateShipmentDetails,
      },
      {
        id: 'link-existing-shipment',
        label: t('ALLOCATIONS_ACTION_LINK_EXISTING_SHIPMENT'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.linkExistingShipment),
        onClick: handleLinkExistingShipment,
      },
      {
        id: 'add-attachments',
        label: t('ACTION_ADD_ATTACHMENTS'),
        variant: ButtonVariants.tertiary,
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.addAttachments),
        onClick: handleAddAttachments,
      },
    ],
    [
      t,
      handleGenerateDeclaration,
      handleDownloadDeclaration,
      handleEditDeclaration,
      handleUpdateShipmentDetails,
      handleLinkExistingShipment,
      handleAddAttachments,
    ],
  );

  const rowMenuActions = useMemo(
    () => [
      {
        id: 'view-declaration',
        label: t('ALLOCATIONS_ACTION_VIEW_DECLARATION'),
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.viewDeclaration),
        onClick: handleViewDeclaration,
      },
      {
        id: 'mark-rejected',
        label: t('ALLOCATIONS_ACTION_MARK_REJECTED'),
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.markRejected),
        onClick: handleMarkRejected,
      },
      {
        id: 'add-attachments-menu',
        label: t('ACTION_ADD_ATTACHMENTS'),
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.addAttachmentsMenu),
        onClick: handleAddAttachments,
      },
      {
        id: 'revoke',
        label: t('ALLOCATIONS_ACTION_REVOKE'),
        hidden: hiddenUnless(ALLOCATION_ACTION_VISIBILITY.revoke),
        onClick: handleRevoke,
      },
    ],
    [t, handleViewDeclaration, handleMarkRejected, handleAddAttachments, handleRevoke],
  );

  // Order-level totals in both units, so they show even when the table is empty or mixed.
  const tiles = useMemo(
    () => [
      {
        label: t('ORDERS_DETAIL_ALLOCATED_QUANTITY'),
        value: allocationsSummary?.allocatedQuantity ?? '-',
      },
      {
        label: t('ORDERS_DETAIL_REMAINING_TO_ALLOCATE'),
        value: allocationsSummary?.remainingToAllocateQuantity ?? '-',
      },
      { label: t('ORDERS_COL_ALLOCATED_GHG_SAVINGS'), value: allocatedGhgSavings },
    ],
    [allocationsSummary, allocatedGhgSavings, t],
  );

  // isError swaps the same Table emptyState slot to the load-error copy instead of the no-rows copy.
  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: isError
        ? t('ORDERS_DETAIL_ALLOCATIONS_LOAD_ERROR')
        : t('ORDERS_DETAIL_ALLOCATIONS_EMPTY_TITLE'),
      description: isError ? null : t('ORDERS_DETAIL_ALLOCATIONS_EMPTY_DESCRIPTION'),
    }),
    [isError, t],
  );

  return (
    <SummaryTiles tiles={tiles}>
      <UnitTypeContext.Provider value={contextValue}>
        <Table
          columns={columns}
          data={rows}
          loading={isFetching}
          getRowId={getAllocationId}
          searchable
          labels={tableLabels}
          rowActions={rowActions}
          rowMenuActions={rowMenuActions}
          actionsHeader={t('FIELD_ACTION')}
          actionsWidth={ACTIONS_WIDTH}
          emptyState={emptyState}
        />
      </UnitTypeContext.Provider>

      <AddAttachmentModal
        open={attachmentRow != null}
        onClose={handleCloseAttachment}
        title={t('ALLOCATIONS_ADD_ATTACHMENT_MODAL_TITLE', {
          transactionId: attachmentRow?.transactionId,
        })}
        target={ATTACHMENT_TARGETS.allocation}
        module={module}
        entityId={attachmentRow?.id}
      />

      <AllocationConfirmDialog module={module} confirm={confirm} onClose={handleCloseConfirm} />
    </SummaryTiles>
  );
};
